import TextButton, {
  TextButtonVariants,
} from '@/components/atoms/TextButton/TextButton';
import Button, { ButtonVariants } from '@/components/atoms/legacy/Button';
import Link from '@/components/atoms/legacy/Link';
import { useAnalytics } from '@/components/molecules/AnalyticsProvider';
import FormInput from '@/components/molecules/FormInput';
import { ProgressBar } from '@/components/molecules/ProgressBar/ProgressBar';
import {
  BusinessInformationFormFields,
  PartnerSignUpFormFields,
} from '@/components/template/PartnerLandingTemplate/types';
import { GOOGLE_TRACK_INFO } from '@/lib/constants';
import { formatFileSize } from '@/lib/formatFileSize';
import { FormErrors, isFormValid } from '@/types/enums';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useCallback, useReducer, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { UploadDealerLicenseModal } from '../UploadDealerLicenseModal/UploadDealerLicenseModal';
import { PartnerSignUpFlowProps } from './ContactInformationForm';

type BusinessInformationFormType = {
  dealership: string;
  businessTitle: string;
  corporationName: string;
  dealerNumber: string;
  expiration: string;
};

type BusinessInformationFormActionType =
  | { type: 'UPDATE_DEALERSHIP'; value: string }
  | { type: 'UPDATE_BUSINESS_TITLE'; value: string }
  | { type: 'UPDATE_CORPORATION_NAME'; value: string }
  | { type: 'UPDATE_DEALER_NUMBER'; value: string }
  | { type: 'UPDATE_EXPIRATION'; value: string };

const reducer = (
  state: BusinessInformationFormType,
  action: BusinessInformationFormActionType
) => {
  switch (action.type) {
    case 'UPDATE_DEALERSHIP':
      return {
        ...state,
        dealership: action.value,
      };
    case 'UPDATE_BUSINESS_TITLE':
      return {
        ...state,
        businessTitle: action.value,
      };
    case 'UPDATE_CORPORATION_NAME':
      return {
        ...state,
        corporationName: action.value,
      };
    case 'UPDATE_DEALER_NUMBER':
      return {
        ...state,
        dealerNumber: action.value,
      };
    case 'UPDATE_EXPIRATION':
      return {
        ...state,
        expiration: action.value,
      };
    default:
      return state;
  }
};

async function uploadFileToS3(presignedUrl: string, file: Blob) {
  return fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
}

interface BusinessInformationFormProps extends PartnerSignUpFlowProps {
  onSubmit: (values: Partial<PartnerSignUpFormFields>) => Promise<void>;
  goBack: (values: Partial<PartnerSignUpFormFields>) => void;
  initialValues: BusinessInformationFormFields;
  onUploadedFile: (uploadedFile: File | null) => void;
}

export const BusinessInformationForm: React.FC<
  BusinessInformationFormProps
> = ({ onSubmit, progress, goBack, onUploadedFile, initialValues }) => {
  const { uploadedFile: uploadedLicense } = initialValues;
  const [uploadLicenseModalOpen, setUploadLicenseModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const { handleActionTracking } = useAnalytics();

  const [formErrors, setFormErrors] = useState({
    dealership: FormErrors.Clear,
    businessTitle: FormErrors.Clear,
    corporationName: FormErrors.Clear,
    dealerNumber: FormErrors.Clear,
    expiration: FormErrors.Clear,
  });

  const [formData, dispatch] = useReducer(reducer, {
    dealership: initialValues.dealership,
    businessTitle: initialValues.businessTitle,
    corporationName: initialValues.corporationName,
    dealerNumber: initialValues.dealerNumber,
    expiration: initialValues.expiration,
  });

  const clearUploadedFile = () => onUploadedFile(null);

  const validateForm = useCallback(() => {
    const currentFormErrors = {
      dealership: formData.dealership ? FormErrors.Clear : FormErrors.Empty,
      businessTitle: formData.businessTitle
        ? FormErrors.Clear
        : FormErrors.Empty,
      ...(!!uploadedLicense && {
        corporationName: formData.corporationName
          ? FormErrors.Clear
          : FormErrors.Empty,
        dealerNumber: formData.dealerNumber
          ? FormErrors.Clear
          : FormErrors.INVALID_DEALER_LICENSE,
        expiration: formData.expiration
          ? FormErrors.Clear
          : FormErrors.INVALID_EXPIRATION_DATE,
      }),
    };
    setFormErrors({ ...formErrors, ...currentFormErrors });
    return isFormValid(currentFormErrors);
  }, [uploadedLicense, formData, formErrors]);

  function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex !== -1 ? fileName.slice(dotIndex) : '';
  }

  const renameFile = useCallback((file: File, newFileName: string) => {
    const fileExtension = getFileExtension(file.name);
    const blob = file.slice(0, file.size, file.type);
    return new File([blob], `${newFileName}${fileExtension}`, {
      type: file.type,
    });
  }, []);

  const handleGoBack = useCallback(() => {
    goBack({
      ...formData,
      uploadedFile: uploadedLicense,
    });
  }, [goBack, formData, uploadedLicense]);

  const handleSubmit = useCallback(async () => {
    const shouldSubmitForm = validateForm();
    setUploadError('');

    if (shouldSubmitForm) {
      let filesToUpload: { id: string; name: string }[] = [];
      handleActionTracking(GOOGLE_TRACK_INFO.partnerInfoSubmitButton);
      setLoading(true);

      try {
        if (uploadedLicense) {
          const fileId = uuidv4();
          const renamedFile = renameFile(uploadedLicense, fileId);

          const response = await fetch(
            `api/partner/files?method=put_object&file_name=${renamedFile.name}&content_type=${renamedFile.type}`
          );

          const { presigned_url: presignedUrl } = await response.json();

          const uploadToS3Resp = await uploadFileToS3(
            presignedUrl,
            new Blob([renamedFile], { type: renamedFile.type })
          );

          if (!uploadToS3Resp.ok) {
            throw new Error('There was a problem uploading your file.');
          }

          filesToUpload = [{ id: fileId, name: renamedFile.name }];
        }
        await onSubmit({
          ...formData,
          files: filesToUpload,
        });
        handleActionTracking(GOOGLE_TRACK_INFO.partnerInfoSubmitSuccess);
      } catch (err) {
        setUploadError(
          'There was a problem submitting your information. Please try again.'
        );
        handleActionTracking(GOOGLE_TRACK_INFO.partnerInfoSubmitFail);
      } finally {
        setLoading(false);
      }
    }
  }, [
    uploadedLicense,
    validateForm,
    formData,
    onSubmit,
    renameFile,
    handleActionTracking,
  ]);

  return (
    <section className="pb-[40px] lg:flex lg:h-full lg:flex-col lg:justify-between">
      {uploadLicenseModalOpen && (
        <UploadDealerLicenseModal
          open={uploadLicenseModalOpen}
          close={() => setUploadLicenseModalOpen(false)}
          onUploadLicense={onUploadedFile}
          uploadedLicense={uploadedLicense}
          clearUploadedLicense={clearUploadedFile}
        />
      )}
      <div className="mb-6 flex flex-col">
        <div className="mb-6">
          <span className="text-title24px font-semibold md:text-display32px lg:text-display48px">
            Enter business information
          </span>
        </div>
        <FormInput
          variant="primary"
          label="Dealership name"
          required
          placeholder="EV Marketplace"
          className="mb-6"
          errorMessage="Please input your dealership name"
          value={formData.dealership}
          onChange={(value: string) => {
            dispatch({ type: 'UPDATE_DEALERSHIP', value });
            setFormErrors({
              ...formErrors,
              dealership: FormErrors.Clear,
            });
          }}
          invalid={formErrors.dealership !== FormErrors.Clear}
        />
        <FormInput
          variant="primary"
          required
          label="Business Title"
          className="mb-6"
          placeholder="Product Outreach Manager"
          errorMessage="Please input your business title"
          value={formData.businessTitle}
          onChange={(value: string) => {
            dispatch({ type: 'UPDATE_BUSINESS_TITLE', value });
            setFormErrors({
              ...formErrors,
              businessTitle: FormErrors.Clear,
            });
          }}
          invalid={formErrors.businessTitle !== FormErrors.Clear}
        />
        {!uploadedLicense ? (
          <Button
            variant={ButtonVariants.newDefault}
            onClick={() => setUploadLicenseModalOpen(true)}
          >
            Upload Dealer License (Optional)
          </Button>
        ) : (
          <>
            <div className="mb-8 text-title20px">
              <span>Dealer license</span>
            </div>
            <div className="mb-6 flex flex-col bg-neutral-100 px-4 py-2">
              <div className="flex flex-row items-center justify-between">
                <span className="text-blue-medium">{uploadedLicense.name}</span>
                <div role="button" onClick={clearUploadedFile}>
                  <XMarkIcon className="size-[20px] stroke-[2.5px] text-neutral-900" />
                </div>
              </div>
              <span>{formatFileSize(uploadedLicense.size)}</span>
            </div>
            <FormInput
              variant="primary"
              label="Corporation Name"
              required
              className="mb-6"
              errorMessage="Please enter a corporation name"
              value={formData.corporationName}
              onChange={(value: string) => {
                dispatch({ type: 'UPDATE_CORPORATION_NAME', value });
                setFormErrors({
                  ...formErrors,
                  corporationName: FormErrors.Clear,
                });
              }}
              invalid={formErrors.corporationName !== FormErrors.Clear}
            />
            <div className="flex flex-row gap-4">
              <FormInput
                variant="primary"
                label="Dealer number"
                required
                className="mb-6 flex-1"
                errorMessage="Please enter a valid dealer number"
                value={formData.dealerNumber}
                onChange={(value: string) => {
                  dispatch({
                    type: 'UPDATE_DEALER_NUMBER',
                    value,
                  });
                  setFormErrors({
                    ...formErrors,
                    dealerNumber: FormErrors.Clear,
                  });
                }}
                invalid={formErrors.dealerNumber !== FormErrors.Clear}
              />
              <FormInput
                variant="primary"
                className="mb-6 flex-1"
                type="date"
                label="Expiration"
                required
                errorMessage="Please enter an expiration date"
                value={formData.expiration}
                onChange={(value: string) => {
                  dispatch({ type: 'UPDATE_EXPIRATION', value });
                  setFormErrors({
                    ...formErrors,
                    expiration: FormErrors.Clear,
                  });
                }}
                invalid={formErrors.expiration !== FormErrors.Clear}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col">
        {uploadError && (
          <div className="mb-6">
            <span className="text-micro12px text-red-medium">
              {uploadError}
            </span>
          </div>
        )}
        <div className="mb-6">
          <span className="text-micro12px text-neutral-600">
            By continuing, you agree to the{' '}
            <Link
              className="text-micro12p font-semibold underline"
              href="terms_and_conditions"
              target="_blank"
            >
              Agreement
            </Link>{' '}
            and{' '}
            <Link
              className="text-micro12p font-semibold underline"
              href="privacy_policy"
              target="_blank"
            >
              Privacy Policy
            </Link>
          </span>
        </div>
        <ProgressBar value={progress} max={100} className="mb-[20px]" />
        <div className="flex flex-row justify-between">
          <TextButton
            onClick={handleGoBack}
            variant={TextButtonVariants.Default}
            aria-label="Go back button"
            icon={{
              position: 'left',
              icon: <ArrowLeftIcon width={20} height={20} />,
            }}
          >
            Back
          </TextButton>

          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={loading}
            variant={ButtonVariants.newPrimary}
          >
            Continue
          </Button>
        </div>
      </div>
    </section>
  );
};
